import { AlertDialogAction } from '@/components/ui/alert-dialog';
import type React from 'react';

interface Props {
  /** Starts the action. Its own `finally` must clear the pending target. */
  onConfirm: () => void;
  isRunning: boolean;
  /** Label while the request is in flight, e.g. "Eliminando...". */
  runningLabel: string;
  /** Extra condition beyond `isRunning`, e.g. a typed-name confirmation. */
  disabled?: boolean;
  children: React.ReactNode;
}

/**
 * The confirm button of a backoffice confirmation dialog.
 *
 * It exists for one line: `event.preventDefault()`. Radix's `AlertDialogAction`
 * is a `DialogPrimitive.Close`, so without it the dialog unmounts on the same
 * click that starts the request and the running state below never renders —
 * and, while the request is still in flight, a second dialog can be opened with
 * its buttons already disabled and then be closed by the first request's
 * `finally`. Four screens needed this and each had rediscovered it separately;
 * the next one should not have to.
 *
 * Closing is left to the caller: clearing the pending target is what shuts the
 * dialog once the action settles.
 */
export const ConfirmDialogAction = ({
  onConfirm,
  isRunning,
  runningLabel,
  disabled = false,
  children,
}: Props) => (
  <AlertDialogAction
    onClick={(event) => {
      event.preventDefault();
      onConfirm();
    }}
    disabled={isRunning || disabled}
  >
    {isRunning ? runningLabel : children}
  </AlertDialogAction>
);
